import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import { DatesSetArg, EventClickArg, EventContentArg } from "@fullcalendar/core"
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction"
import { DayGridContent } from "./DayGridContent"
import { InlineFallbackContents } from "../errors/InlineErrorBoundary"
import { ErrorBoundary } from "react-error-boundary"
import { TimeGridContent } from "./TimeGridContent"
import { AdditionalEventMenuItem } from "../../../modules/nonAdminUser/reports/home/components/upcomingOrders/meetings/MeetingCardMenu"
import { CalendarEvent } from "./calendarTypes"
import { useMemo } from "react"
import { MenuCalendarEvent } from "../../../modules/merchant/merchant/menuPlanning/pages/MenuCalendar"

type StyleProps = Readonly<{
    isEventClick: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isEventClick }) => ({
    container: {
        width: "100%",
        maxWidth: "70rem",
        margin: "0 auto",

        "& .fc": {
            "& button, & .fc-toolbar-chunk:last-child button": {
                backgroundColor: theme.palette.tailwind.sky[700],
                color: "white",
            },
            "& button:hover, & .fc-toolbar-chunk:last-child button:hover": {
                backgroundColor: theme.palette.tailwind.sky[800],
            },
            "& button:active, & .fc-toolbar-chunk:last-child .fc-button-active": {
                backgroundColor: theme.palette.tailwind.sky[900],
            },
        },
        ".fc-event:hover": {
            cursor: isEventClick ? "pointer" : "default",
        },
        ".fc-daygrid-dot-event:hover": {
            background: "inherit",
        },
        ".fc-v-event": {
            background: "none",
            border: "none",
        },
        ".fc-event-main > div": {
            height: "100%",
        },
        ".fc-event-main > div > div": {
            height: "fit-content",
        },
    },
    errorBoundary: {
        background: theme.palette.tailwind.slate[50],
        padding: "10%",
    },
}))

type StandardCalendarProps = Readonly<{
    events: CalendarEvent[] | MenuCalendarEvent[]
    additionalMenuItems?: AdditionalEventMenuItem[]
    handleEventClick?: (arg: EventClickArg) => void
    handleDatesSet: (arg: DatesSetArg) => void
    handleDateClick?: ((arg: DateClickArg) => void) | undefined
    //
    dayGrid?: ((arg: EventContentArg) => JSX.Element) | null
    timeGrid?: ((arg: EventContentArg) => JSX.Element) | null
}>

export function StandardCalendar({
    additionalMenuItems,
    events,
    handleEventClick,
    handleDatesSet,
    handleDateClick,
    dayGrid,
    timeGrid,
}: StandardCalendarProps) {
    const { classes } = useStyles({ isEventClick: !!handleEventClick })

    const defaultDayGridEventContent = (arg: EventContentArg) => (
        <ErrorBoundary
            fallback={
                <InlineFallbackContents
                    errorMessageId="inline-error-boundary-calendar-event-message"
                    externalClasses={classes.errorBoundary}
                />
            }
        >
            {/* strictly use CalendarEvent as it's default view */}
            <DayGridContent
                events={events as CalendarEvent[]}
                additionalMenuItems={additionalMenuItems}
                event={arg.event}
            />
        </ErrorBoundary>
    )

    const defaultTimeGridEventContent = (arg: EventContentArg) => (
        <ErrorBoundary
            fallback={
                <InlineFallbackContents
                    errorMessageId="inline-error-boundary-calendar-event-message"
                    externalClasses={classes.errorBoundary}
                />
            }
        >
            {/* strictly use CalendarEvent as it's default view */}
            <TimeGridContent events={events as CalendarEvent[]} event={arg.event} />
        </ErrorBoundary>
    )

    const views = {
        ...(dayGrid !== null && {
            dayGrid: {
                eventContent: dayGrid ?? defaultDayGridEventContent,
            },
        }),
        ...(timeGrid !== null && {
            timeGrid: {
                eventContent: timeGrid ?? defaultTimeGridEventContent,
            },
        }),
    }

    const baseEvents = useMemo(() => events.map((event) => ({ ...event, id: event.id.toString() })), [events])

    return (
        <Box className={classes.container}>
            <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                views={views}
                initialView="dayGridMonth"
                events={baseEvents}
                themeSystem="bootstrap"
                eventClick={handleEventClick}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
            />
        </Box>
    )
}
